import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Label, Input, Textarea, Select, StatusBadge } from "@/components/shared";
import { OFFICE_STATUSES } from "@/components/OfficeNotes";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, MapPin, Search, Sparkles, Upload, FileUp, MessageSquare } from "lucide-react";
import { ImportDialog } from "@/components/ImportDialog";

const SERVICE_TYPES = ["Property Preservation", "Initial Services", "Inspection", "Occupancy Check", "Eviction", "REO Services", "Maid Refresh / Janitorial", "Lock Changes / Re-Key", "Debris Removal", "Winterization", "De-Winterization", "Grass Cut / Lawn Maintenance", "Securing / Board-Up", "Pool Securing", "Repairs", "Virtual Tour", "Mobile Notary", "Real Estate Photography", "Construction"];
const STATUSES = ["active", "in_progress", "completed"];

const EMPTY_FORM = { address: "", client_name: "", client_id: "", source_client_id: "", source_client_name: "", service_type: SERVICE_TYPES[0], status: "active", office_status: "", office_status_other: "", scope: "", scheduled_date: "", assigned_crew_id: "", work_order: "", due_date: "", lock_code: "", allowable_payout: "", price_sheet_id: "", special_instructions: "" };

export default function Jobs({ archived = false }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [crew, setCrew] = useState([]);
  const [clients, setClients] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [payPreview, setPayPreview] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [wo, setWo] = useState(null);
  const [parsing, setParsing] = useState(false);

  const parseWorkOrder = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/ai/parse-work-order", fd);
      setWo(data);
      const f = data.fields || {};
      const match = clients.find((c) => f.client_name && (c.company || c.name || "").toLowerCase().includes(String(f.client_name).toLowerCase().split(" ")[0]));
      setForm((p) => ({
        ...p,
        address: f.address || p.address, client_name: f.client_name || p.client_name,
        source_client_id: match ? match.id : p.source_client_id, source_client_name: match ? (match.company || match.name) : p.source_client_name,
        service_type: SERVICE_TYPES.includes(f.service_type) ? f.service_type : p.service_type,
        scope: f.scope || p.scope, special_instructions: f.special_instructions || p.special_instructions,
        work_order: f.work_order || p.work_order, due_date: (f.due_date || "").slice(0, 10) || p.due_date,
        scheduled_date: (f.scheduled_date || f.due_date || "").slice(0, 10) || p.scheduled_date, lock_code: f.lock_code || p.lock_code,
      }));
      toast.success(data.missing?.length ? `Work order read — fill in: ${data.missing.join(", ")}` : "Work order read — review and create");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setParsing(false); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/jobs", { params: { archived } });
    setJobs(data);
  }, [archived]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user?.role === "admin") { api.get("/crew").then((r) => setCrew(r.data)); api.get("/client-accounts").then((r) => setClients(r.data)); api.get("/pricing/sheets").then((r) => setSheets(r.data)).catch(() => {}); } }, [user]);

  const applyPricing = async (sheetId, service) => {
    if (!sheetId) { setPayPreview(null); return; }
    try {
      const { data } = await api.get("/pricing/match", { params: { sheet_id: sheetId, service: service || "" } });
      if (data.matched) { setForm((f) => ({ ...f, allowable_payout: data.matched.client_price ?? f.allowable_payout })); setPayPreview(data.matched); }
      else setPayPreview({ none: true });
    } catch { setPayPreview(null); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, client_id: form.client_id || null, source_client_id: form.source_client_id || null, assigned_crew_id: form.assigned_crew_id || null, scheduled_date: form.scheduled_date || null,
        due_date: form.due_date || null, work_order: form.work_order || null, lock_code: form.lock_code || null, price_sheet_id: form.price_sheet_id || null,
        allowable_payout: form.allowable_payout === "" ? null : Number(form.allowable_payout),
        document_file_id: wo?.file_id || null, document_filename: wo?.filename || null };
      await api.post("/jobs", payload);
      toast.success("Job created");
      setOpen(false);
      setForm(EMPTY_FORM); setWo(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filtered = jobs.filter((j) => (filter === "all" || j.status === filter) && (!q || `${j.address} ${j.client_name} ${j.service_type}`.toLowerCase().includes(q.toLowerCase())));

  const aiDraftScope = async () => {
    if (!form.address && !form.scope) { toast.error("Enter an address or short description first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/draft-scope", { description: form.scope || `${form.service_type} at ${form.address}`, service_type: form.service_type, address: form.address });
      setForm((f) => ({ ...f, scope: data.scope }));
      toast.success("AI drafted the scope");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 animate-rise">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">{archived ? t("jobsPage.archive") : user?.role === "admin" ? t("jobsPage.allProps") : t("jobsPage.assignedWork")}</div>
          <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">{archived ? t("jobsPage.archivedTitle") : t("jobsPage.title")}</h1>
        </div>
        {user?.role === "admin" && !archived && (
          <div className="flex gap-2 flex-wrap">
            <Btn variant="secondary" data-testid="import-jobs-btn" onClick={() => setImportOpen(true)}><Upload size={16} /> {t("jobsPage.importCrm")}</Btn>
            <Btn data-testid="create-job-btn" onClick={() => setOpen(true)}><Plus size={16} /> {t("jobsPage.newJob")}</Btn>
          </div>
        )}
      </div>
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} idPrefix="jobs" title="Bulk Import Jobs" endpoint="/imports/jobs"
        templateName="jobs_import_template.csv" buttonLabel="Import Jobs" onDone={load}
        description="Upload a work-order export from your CRM (.csv or .xlsx). Each row becomes a job. Rows with a Contractor Email matching an existing contractor are auto-assigned; the rest alert nearby available contractors. Duplicate Work Order #s are skipped."
        columns="Address · Client Name · Service Type · Status · Scope of Work · Scheduled Date · Contractor Email · Allowable Payout · Work Order #" />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <Input data-testid="job-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("jobsPage.search")} className="pl-9" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", ...STATUSES].map((s) => (
            <button key={s} data-testid={`job-filter-${s}`} onClick={() => setFilter(s)}
              className={`font-mono text-xs uppercase tracking-wide px-3 py-2 border-2 transition-all ${filter === s ? "bg-[#FF5722] text-black border-black" : "bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-600"}`}>
              {s === "all" ? t("jobsPage.all") : t(`status.${s}`, s.replace("_", " "))}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <Card className="p-10 text-center text-zinc-600 font-mono">{t("jobsPage.none")}</Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((j, i) => (
            <Card key={j.id} data-testid={`job-card-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)}
              className="p-5 cursor-pointer hover:-translate-y-1 hover:shadow-[6px_6px_0px_#FF5722] transition-all animate-rise" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722] bg-[#FF5722]/10 border border-[#FF5722]/30 px-2 py-0.5">{j.service_type}</span>
                <div className="flex flex-col items-end gap-1">
                  <StatusBadge status={j.archived ? "archived" : j.status} />
                  {j.unread_chat > 0 && <span data-testid={`chat-badge-${j.id}`} className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider bg-[#FF5722] text-black border border-black px-1.5 py-0.5"><MessageSquare size={10} /> {j.unread_chat} new</span>}
                  {j.ready_for_invoicing && <span className="font-mono text-[9px] uppercase tracking-wider bg-emerald-500 text-black border border-black px-1.5 py-0.5">Invoice Ready</span>}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin size={16} className="text-zinc-500 mt-0.5 shrink-0" />
                <h3 className="font-head font-bold text-white leading-tight">{j.address}</h3>
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between text-xs font-mono text-zinc-500">
                <span>{j.source_client_name || j.client_name || "—"}</span>
                <span className="text-zinc-400">{j.assigned_crew_name || "Unassigned"}</span>
              </div>
              {user?.role === "crew" && (j.contractor_payout != null || j.contractor_pay != null) && (
                <div className="mt-2 font-mono text-[11px] uppercase tracking-wide text-emerald-400 border border-emerald-900/60 bg-emerald-950/20 px-2 py-1 inline-block" data-testid={`card-payout-${j.id}`}>
                  Payout: ${Number(j.contractor_payout ?? j.contractor_pay).toFixed(2)}{j.contractor_payout != null ? " agreed" : ""}
                </div>
              )}
              {j.scheduled_date && <div className="mt-1 font-mono text-[11px] text-zinc-600">📅 {new Date(j.scheduled_date).toLocaleDateString()}</div>}
            </Card>
          ))}
        </div>
      )}

      {/* Create modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white max-w-lg max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader><DialogTitle className="font-head font-black uppercase tracking-tight">New Job</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="border-2 border-dashed border-zinc-800 p-3 bg-black/30" data-testid="wo-upload-box">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722]">Have the client's work order?</div>
                  <div className="text-xs text-zinc-400">Upload the PDF — we'll read it and fill the form. Fix anything missing before you create the job.</div>
                </div>
                <label className={`inline-flex items-center gap-1.5 px-3 py-2 border-2 border-black bg-[#FF5722] text-black font-bold uppercase text-xs cursor-pointer ${parsing ? "opacity-60 pointer-events-none" : ""}`}>
                  <FileUp size={14} /> {parsing ? "Reading…" : wo ? "Replace PDF" : "Upload work order"}
                  <input data-testid="wo-file-input" type="file" accept=".pdf,image/*" className="hidden" onChange={parseWorkOrder} />
                </label>
              </div>
              {wo && (
                <div className="mt-2 font-mono text-[11px] text-zinc-400" data-testid="wo-parsed">
                  <span className="text-emerald-400">✓ {wo.filename}</span> attached to this job.
                  {wo.missing?.length > 0 && <span className="block text-amber-400 mt-0.5">Couldn't find: {wo.missing.join(", ")} — please fill in below.</span>}
                  {!wo.text_found && <span className="block text-amber-400 mt-0.5">No readable text in that file — fill in the details manually; the file will still be attached.</span>}
                </div>
              )}
            </div>
            <div><Label>Property Address</Label><Input data-testid="job-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required /></div>
            <div><Label>Requested By (Client)</Label><Select data-testid="job-source-client" value={form.source_client_id || (form.client_id ? `u:${form.client_id}` : "")} onChange={(e) => { const c = clients.find((x) => x.id === e.target.value); setForm({ ...form, source_client_id: c?.source_client_id || null, source_client_name: c ? (c.company || c.name) : "", client_name: c ? (c.company || c.name) : form.client_name, client_id: c?.user_id || null }); }}><option value="">— Select client —</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.company ? `${c.company} (${c.name})` : c.name}</option>)}</Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Service Type</Label><Select data-testid="job-service" value={form.service_type} onChange={(e) => { setForm({ ...form, service_type: e.target.value }); applyPricing(form.price_sheet_id, e.target.value); }}>{SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}</Select></div>
              <div><Label>Status</Label><Select data-testid="job-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</Select></div>
            </div>
            <div><Label>{t("jobsPage.priceSheet")}</Label><Select data-testid="job-price-sheet" value={form.price_sheet_id} onChange={(e) => { setForm({ ...form, price_sheet_id: e.target.value }); applyPricing(e.target.value, form.service_type); }}><option value="">— None —</option>{sheets.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
              {payPreview && !payPreview.none && <p className="mt-1 font-mono text-[11px] text-emerald-400" data-testid="job-pay-preview">{payPreview.service}: client ${Number(payPreview.client_price ?? 0).toFixed(2)} → contractor pays ${Number(payPreview.contractor_pay ?? 0).toFixed(2)}</p>}
              {payPreview?.none && <p className="mt-1 font-mono text-[11px] text-amber-400" data-testid="job-pay-preview">No sheet line matches "{form.service_type}".</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Office / Invoice Status</Label><Select data-testid="job-office-status" value={form.office_status || ""} onChange={(e) => setForm({ ...form, office_status: e.target.value })}>{OFFICE_STATUSES.map((s) => <option key={s} value={s}>{s || "— Not set —"}</option>)}</Select></div>
              {form.office_status === "Other" && <div><Label>Specify</Label><Input data-testid="job-office-other" value={form.office_status_other || ""} onChange={(e) => setForm({ ...form, office_status_other: e.target.value })} /></div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Scheduled Date</Label><Input data-testid="job-date" type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} /></div>
              <div><Label>Assign Crew</Label><Select data-testid="job-crew" value={form.assigned_crew_id} onChange={(e) => setForm({ ...form, assigned_crew_id: e.target.value })}><option value="">Unassigned</option>{crew.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Scope / Notes</Label>
                <button type="button" data-testid="job-ai-scope-btn" onClick={aiDraftScope} disabled={aiBusy} className="flex items-center gap-1 text-[#FF5722] hover:text-[#F4511E] font-mono text-[10px] uppercase tracking-widest disabled:opacity-50 mb-1.5"><Sparkles size={12} /> {aiBusy ? "Drafting…" : "AI Draft"}</button>
              </div>
              <Textarea data-testid="job-scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Work Order #</Label><Input data-testid="job-work-order" value={form.work_order} onChange={(e) => setForm({ ...form, work_order: e.target.value })} /></div>
              <div><Label>Client Due Date</Label><Input data-testid="job-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div><Label>Lock / Key Codes</Label><Input data-testid="job-lock" value={form.lock_code} onChange={(e) => setForm({ ...form, lock_code: e.target.value })} placeholder="LB:1234 KEY:5678" /></div>
              <div><Label>Allowable Payout ($)</Label><Input data-testid="job-allowable" type="number" step="0.01" value={form.allowable_payout} onChange={(e) => setForm({ ...form, allowable_payout: e.target.value })} /></div>
            </div>
            <div><Label>Special Instructions (client requirements shown to the contractor)</Label><Textarea data-testid="job-special" value={form.special_instructions} onChange={(e) => setForm({ ...form, special_instructions: e.target.value })} placeholder="Photo specs, deadlines, permits, who to call from site…" /></div>
            <Btn data-testid="job-create-submit" type="submit" className="w-full">Create Job</Btn>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
